import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { SegmentedControl } from '../components/SegmentedControl';
import { TripCard } from '../components/TripCard';
import { BookingRequest } from '../App';
interface TripsProps {
  bookings: BookingRequest[];
  onTripClick: (tripId: string) => void;
  onStartBooking?: (tripId: string) => void;
  onReviewInspect?: (tripId: string) => void;
  completedTripIds?: string[];
}
export function Trips({
  bookings,
  onTripClick,
  onStartBooking,
  onReviewInspect,
  completedTripIds = []
}: TripsProps) {
  const [selectedTab, setSelectedTab] = useState('Active');
  // Convert BookingRequest to TripCard format
  const convertBookingsToTrips = (bookingsList: BookingRequest[]) => {
    return bookingsList.map(booking => ({
      id: booking.id,
      itemTitle: booking.itemTitle,
      itemImage: booking.itemImage,
      startDate: booking.startDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      }),
      endDate: booking.endDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      }),
      status: booking.status as any,
      location: booking.location
    }));
  };
  const mockTrips = {
    Active: [{
      id: '1',
      itemTitle: 'Professional Power Drill Set',
      itemImage: 'https://images.unsplash.com/photo-1504148455328-c376907d081c?w=400&q=80',
      startDate: 'Dec 15',
      endDate: 'Dec 18',
      status: 'active' as const,
      location: '0.3 mi away'
    }, {
      id: '5',
      itemTitle: 'Portable Projector HD',
      itemImage: 'https://images.unsplash.com/photo-1478720568477-152d9b164e26?w=400&q=80',
      startDate: 'Dec 12',
      endDate: 'Dec 16',
      status: 'returned' as const,
      location: '0.5 mi away'
    }],
    Upcoming: [{
      id: '2',
      itemTitle: 'Camping Tent (6-person)',
      itemImage: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=400&q=80',
      startDate: 'Dec 20',
      endDate: 'Dec 23',
      status: 'approved' as const,
      location: '0.8 mi away'
    }, {
      id: '3',
      itemTitle: 'Baby Stroller - Lightweight',
      itemImage: 'https://images.unsplash.com/photo-1519340241574-2cec6aef0c01?w=400&q=80',
      startDate: 'Dec 25',
      endDate: 'Dec 27',
      status: 'pending' as const,
      location: '0.5 mi away'
    }],
    Past: [{
      id: '4',
      itemTitle: 'Folding Tables & Chairs Set',
      itemImage: 'https://images.unsplash.com/photo-1505236858219-8359eb29e329?w=400&q=80',
      startDate: 'Dec 1',
      endDate: 'Dec 3',
      status: 'completed' as const,
      location: '1.2 mi away'
    }],
    All: [] as any[]
  };
  // Move completed mock trips from Active to Past
  if (completedTripIds.length > 0) {
    const completedFromActive = mockTrips.Active.filter(t => completedTripIds.includes(t.id));
    mockTrips.Active = mockTrips.Active.filter(t => !completedTripIds.includes(t.id));
    completedFromActive.forEach(t => {
      mockTrips.Past.push({
        ...t,
        status: 'completed' as any
      });
    });
  }
  // Add user bookings to the appropriate categories
  const userTrips = convertBookingsToTrips(bookings);
  userTrips.forEach(trip => {
    if (trip.status === 'active') {
      mockTrips.Active.push(trip);
    } else if (trip.status === 'pending' || trip.status === 'approved') {
      mockTrips.Upcoming.push(trip);
    } else if (trip.status === 'completed') {
      mockTrips.Past.push(trip);
    }
  });
  mockTrips.All = [...mockTrips.Active, ...mockTrips.Upcoming, ...mockTrips.Past];
  const currentTrips = mockTrips[selectedTab as keyof typeof mockTrips];
  // Determine action label and handler based on status
  const getActionForTrip = (status: string) => {
    switch (status) {
      case 'active':
        return {
          label: 'Start Booking',
          handler: onStartBooking
        };
      case 'approved':
        return {
          label: 'Start Booking',
          handler: onStartBooking
        };
      case 'returned':
        return {
          label: 'Review & Inspect',
          handler: onReviewInspect
        };
      default:
        return {
          label: undefined,
          handler: undefined
        };
    }
  };
  const container = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const item = {
    hidden: {
      opacity: 0,
      y: 15
    },
    show: {
      opacity: 1,
      y: 0
    }
  };
  return <div className="min-h-screen bg-cream pb-24" data-id="element-1686">
      {/* Header */}
      <div className="bg-white border-b border-sand sticky top-0 z-10" data-id="element-1687">
        <div className="max-w-md mx-auto px-3 pt-3 pb-3" data-id="element-1688">
          <h1 className="text-base font-bold text-charcoal mb-2" data-id="element-1689">
            My Bookings
          </h1>
          <SegmentedControl options={['Active', 'Upcoming', 'Past', 'All']} selected={selectedTab} onChange={setSelectedTab} data-id="element-1690" />
        </div>
      </div>

      {/* Booking Cards */}
      <motion.div key={selectedTab} variants={container} initial="hidden" animate="show" className="max-w-md mx-auto px-3 py-3 space-y-2" data-id="element-1691">
        {currentTrips.length === 0 ? <div className="text-center py-12" data-id="element-1692">
            <p className="text-sm text-moss-gray" data-id="element-1693">
              No {selectedTab.toLowerCase()} bookings
            </p>
          </div> : currentTrips.map(trip => {
        const action = getActionForTrip(trip.status);
        return <motion.div key={trip.id} variants={item} data-id="element-1694">
                <TripCard {...trip} onClick={() => onTripClick(trip.id)} actionLabel={action.label} onAction={action.label && action.handler ? () => action.handler!(trip.id) : undefined} data-id="element-1695" />
              </motion.div>;
      })}
      </motion.div>
    </div>;
}